declare global {
  interface Window {
    wc_doc_ready?: () => void;
    $wc_leads?: {
      doc?: {
        url?: string;
        ref?: string;
        search?: string;
        hash?: string;
      };
    };
  }
}

// WhatConverts initializes once on hard load: it freezes `$wc_leads.doc`, then
// reads the visitor's number-swap config and replaces the practice's default
// number(s) with the matching tracking number(s) across the DOM. SPA navigations
// skip both, and the tracker's swap pass is guarded so it only ever runs once per
// script load — re-invoking `wc_doc_ready()` is a no-op and does NOT re-swap.
//
// So on every page after the first, the freshly-rendered default number is left
// un-swapped, even though a hard reload swaps it correctly. We close the gap by
// re-pointing `$wc_leads.doc` at the current URL (for correct lead attribution)
// and re-running the swap ourselves, driven by the same config WhatConverts
// persisted to localStorage.

// WhatConverts stores the resolved swap config in `localStorage['wc_swap']` as
// `tracking+..+default+..+profileId` (repeated per number pool). Parse it into
// {default, tracking} digit pairs.
interface SwapPair {
  def: string;
  tracking: string;
}

function readSwapPairs(): SwapPair[] {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem("wc_swap");
  } catch {
    // localStorage can throw in privacy modes — nothing to swap then.
    return [];
  }
  if (!raw) return [];

  const parts = raw
    .split("+..+")
    .map((s) => s.trim())
    .filter(Boolean);

  const pairs: SwapPair[] = [];
  for (let i = 0; i + 1 < parts.length; i += 3) {
    const tracking = parts[i].replace(/\D/g, "");
    const def = parts[i + 1].replace(/\D/g, "");
    // Only swap when both are real, equal-length phone numbers — guards
    // against partial/garbled config and keeps the digit-for-digit reformat
    // below well-defined.
    if (tracking.length >= 7 && def.length === tracking.length) {
      pairs.push({ def, tracking });
    }
  }
  return pairs;
}

// A regex that matches the default number's digits with any non-digit
// separators between them, so it catches every rendered format
// (`7033278200`, `703.327.8200`, `+1 703-327-8200`, …) in one pass.
function buildMatcher(defDigits: string): RegExp {
  return new RegExp(defDigits.split("").join("\\D*"), "g");
}

// Re-emit a matched default number using the tracking digits while preserving
// the original separators, so formatting is kept intact.
function reformat(matched: string, trackingDigits: string): string {
  let di = 0;
  let out = "";
  for (const ch of matched) {
    out += /\d/.test(ch) ? trackingDigits[di++] : ch;
  }
  return out;
}

function swapNumbers(pairs: SwapPair[]): void {
  if (!pairs.length) return;

  for (const { def, tracking } of pairs) {
    // 1) Visible text. Walk text nodes so we only touch rendered copy and
    //    never blow away element structure with innerHTML rewrites.
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT
    );
    const matches: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const text = node as Text;
      if (text.nodeValue && buildMatcher(def).test(text.nodeValue)) {
        matches.push(text);
      }
    }
    for (const text of matches) {
      text.nodeValue = (text.nodeValue || "").replace(buildMatcher(def), (m) =>
        reformat(m, tracking)
      );
    }

    // 2) `tel:` hrefs (including `+1`-prefixed variants — the matcher starts
    //    at the first default digit, leaving any country-code prefix intact).
    for (const a of Array.from(
      document.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]')
    )) {
      const href = a.getAttribute("href") || "";
      if (buildMatcher(def).test(href)) {
        a.setAttribute(
          "href",
          href.replace(buildMatcher(def), (m) => reformat(m, tracking))
        );
      }
    }
  }
}

// Run the swap now and on a couple of short delays so it also catches content
// that renders just after the route change (footer, lazy sections). The swap is
// idempotent — once the default is gone, later passes are no-ops.
function scheduleSwap(): void {
  const pairs = readSwapPairs();
  if (!pairs.length) return;

  const run = () => swapNumbers(pairs);
  requestAnimationFrame(run);
  setTimeout(run, 300);
  setTimeout(run, 1000);
}

export function retrackWhatConverts(_url: string): void {
  if (typeof window === "undefined") return;

  // Keep lead attribution pointed at the page the visitor is actually on.
  if (window.$wc_leads?.doc) {
    window.$wc_leads.doc.url = window.location.href;
    window.$wc_leads.doc.search = window.location.search;
    window.$wc_leads.doc.hash = window.location.hash;
  }

  // Re-apply the number swap ourselves. `wc_doc_ready()` is intentionally not
  // called here: its swap pass is one-shot and re-invoking it does nothing.
  scheduleSwap();
}
